/**
 * Market Map Service
 * API calls for market maps (admin-created maps)
 */
import api from './api';

// Helper to check if user is authenticated
const isAuthenticated = () => {
  return !!localStorage.getItem('access_token');
};

const marketMapService = {
  // ============================================
  // PUBLIC ENDPOINTS (for all users - guests use /public prefix)
  // ============================================

  /**
   * Get active market map by type
   * @param {string} type - 'cho_thue' or 'mua_ban'
   */
  getActiveMap: async (type) => {
    const endpoint = isAuthenticated()
      ? `/market-maps/active/${type}`
      : `/market-maps/public/active/${type}`;
    const response = await api.get(endpoint);
    return response.data;
  },

  /**
   * Get markers for a specific map
   * @param {number} mapId - Market map ID
   */
  getMarkers: async (mapId) => {
    const endpoint = isAuthenticated()
      ? `/market-maps/${mapId}/markers`
      : `/market-maps/public/${mapId}/markers`;
    const response = await api.get(endpoint);
    return response.data;
  },

  /**
   * Get market map settings
   */
  getSettings: async () => {
    const endpoint = isAuthenticated()
      ? '/market-maps/settings'
      : '/market-maps/public/settings';
    const response = await api.get(endpoint);
    return response.data;
  },

  /**
   * Get all active market maps (for dynamic tabs)
   * Returns maps grouped by type
   */
  getAllActiveMaps: async () => {
    const endpoint = isAuthenticated()
      ? '/market-maps/all-active'
      : '/market-maps/public/all-active';
    const response = await api.get(endpoint);
    return response.data;
  },

  /**
   * Get map by ID (with markers) - Public endpoint for all users
   * @param {number} mapId - Market map ID
   */
  getMapById: async (mapId) => {
    const endpoint = isAuthenticated()
      ? `/market-maps/${mapId}`
      : `/market-maps/public/${mapId}`;
    const response = await api.get(endpoint);
    return response.data;
  },

  // ============================================
  // ADMIN ENDPOINTS
  // ============================================

  /**
   * Get all market maps (admin only)
   * @param {object} params - Query params (page, per_page, map_type, is_active)
   */
  getAllMaps: async (params = {}) => {
    const response = await api.get('/market-maps/admin', { params });
    return response.data;
  },

  /**
   * Create new market map (admin only)
   * @param {object} data - Map data
   */
  createMap: async (data) => {
    const response = await api.post('/market-maps/admin', data);
    return response.data;
  },

  /**
   * Get market map detail (admin only)
   * @param {number} mapId - Market map ID
   */
  getMapDetail: async (mapId) => {
    const response = await api.get(`/market-maps/admin/${mapId}`);
    return response.data;
  },

  /**
   * Update market map (admin only)
   * @param {number} mapId - Market map ID
   * @param {object} data - Updated data
   */
  updateMap: async (mapId, data) => {
    const response = await api.put(`/market-maps/admin/${mapId}`, data);
    return response.data;
  },

  /**
   * Delete market map (admin only)
   * @param {number} mapId - Market map ID
   */
  deleteMap: async (mapId) => {
    const response = await api.delete(`/market-maps/admin/${mapId}`);
    return response.data;
  },

  /**
   * Tạo bản sao 1 bản đồ (clone map + markers + layers + shapes) (admin only)
   * @param {number} mapId - ID bản đồ nguồn
   * @param {string} [name] - tên bản sao (mặc định "<tên> (Bản sao)")
   */
  cloneMap: async (mapId, name) => {
    const response = await api.post(`/market-maps/admin/${mapId}/clone`, name ? { name } : {});
    return response.data;
  },

  /**
   * Activate a market map (admin only)
   * @param {number} mapId - Market map ID
   */
  activateMap: async (mapId) => {
    const response = await api.post(`/market-maps/admin/${mapId}/activate`);
    return response.data;
  },

  /**
   * Deactivate a market map (admin only)
   * @param {number} mapId - Market map ID
   */
  deactivateMap: async (mapId) => {
    const response = await api.delete(`/market-maps/admin/${mapId}/activate`);
    return response.data;
  },

  /**
   * Update market map settings (admin only)
   * @param {object} settings - Settings object
   */
  updateSettings: async (settings) => {
    const response = await api.put('/market-maps/admin/settings', settings);
    return response.data;
  },

  // ============================================
  // MARKER ENDPOINTS (Admin only)
  // ============================================

  /**
   * Get all markers for a map (admin only - includes inactive)
   * @param {number} mapId - Market map ID
   */
  getAdminMarkers: async (mapId) => {
    const response = await api.get(`/market-maps/admin/${mapId}/markers`);
    return response.data;
  },

  /**
   * Create new marker (admin only)
   * @param {number} mapId - Market map ID
   * @param {object} data - Marker data
   */
  createMarker: async (mapId, data) => {
    const response = await api.post(`/market-maps/admin/${mapId}/markers`, data);
    return response.data;
  },

  /**
   * Update marker (admin only)
   * @param {number} mapId - Market map ID
   * @param {number} markerId - Marker ID
   * @param {object} data - Updated data
   */
  updateMarker: async (mapId, markerId, data) => {
    const response = await api.put(`/market-maps/admin/${mapId}/markers/${markerId}`, data);
    return response.data;
  },

  /**
   * Delete marker (admin only)
   * @param {number} mapId - Market map ID
   * @param {number} markerId - Marker ID
   */
  deleteMarker: async (mapId, markerId) => {
    const response = await api.delete(`/market-maps/admin/${mapId}/markers/${markerId}`);
    return response.data;
  },

  /**
   * Bulk create markers (admin only)
   * @param {number} mapId - Market map ID
   * @param {array} markers - Array of marker data
   */
  bulkCreateMarkers: async (mapId, markers) => {
    const response = await api.post(`/market-maps/admin/${mapId}/markers/bulk`, { markers });
    return response.data;
  },

  /**
   * Reorder markers (admin only)
   * @param {number} mapId - Market map ID
   * @param {array} markerIds - Ordered array of marker IDs
   */
  reorderMarkers: async (mapId, markerIds) => {
    const response = await api.put(`/market-maps/admin/${mapId}/markers/reorder`, { marker_ids: markerIds });
    return response.data;
  },

  // ============================================
  // LAYER ENDPOINTS (Admin only)
  // ============================================

  /**
   * Get all layers for a map
   * @param {number} mapId - Market map ID
   */
  getLayers: async (mapId) => {
    const response = await api.get(`/market-maps/admin/${mapId}/layers`);
    return response.data;
  },

  /**
   * Create new layer
   * @param {number} mapId - Market map ID
   * @param {object} data - Layer data
   */
  createLayer: async (mapId, data) => {
    const response = await api.post(`/market-maps/admin/${mapId}/layers`, data);
    return response.data;
  },

  /**
   * Update layer
   * @param {number} mapId - Market map ID
   * @param {number} layerId - Layer ID
   * @param {object} data - Updated data
   */
  updateLayer: async (mapId, layerId, data) => {
    const response = await api.put(`/market-maps/admin/${mapId}/layers/${layerId}`, data);
    return response.data;
  },

  /**
   * Delete layer
   * @param {number} mapId - Market map ID
   * @param {number} layerId - Layer ID
   */
  deleteLayer: async (mapId, layerId) => {
    const response = await api.delete(`/market-maps/admin/${mapId}/layers/${layerId}`);
    return response.data;
  },

  /**
   * Reorder layers
   * @param {number} mapId - Market map ID
   * @param {array} layerIds - Ordered array of layer IDs
   */
  reorderLayers: async (mapId, layerIds) => {
    const response = await api.post(`/market-maps/admin/${mapId}/layers/reorder`, { layer_ids: layerIds });
    return response.data;
  },

  // ============================================
  // SHAPE ENDPOINTS (Admin only)
  // ============================================

  /**
   * Get all shapes for a map
   * @param {number} mapId - Market map ID
   * @param {number} layerId - Optional layer filter
   */
  getShapes: async (mapId, layerId = null) => {
    const params = layerId ? { layer_id: layerId } : {};
    const response = await api.get(`/market-maps/admin/${mapId}/shapes`, { params });
    return response.data;
  },

  /**
   * Create new shape
   * @param {number} mapId - Market map ID
   * @param {object} data - Shape data
   */
  createShape: async (mapId, data) => {
    const response = await api.post(`/market-maps/admin/${mapId}/shapes`, data);
    return response.data;
  },

  /**
   * Update shape
   * @param {number} mapId - Market map ID
   * @param {number} shapeId - Shape ID
   * @param {object} data - Updated data
   */
  updateShape: async (mapId, shapeId, data) => {
    const response = await api.put(`/market-maps/admin/${mapId}/shapes/${shapeId}`, data);
    return response.data;
  },

  /**
   * Delete shape
   * @param {number} mapId - Market map ID
   * @param {number} shapeId - Shape ID
   */
  deleteShape: async (mapId, shapeId) => {
    const response = await api.delete(`/market-maps/admin/${mapId}/shapes/${shapeId}`);
    return response.data;
  },

  /**
   * Bulk create shapes
   * @param {number} mapId - Market map ID
   * @param {array} shapes - Array of shape data
   */
  bulkCreateShapes: async (mapId, shapes) => {
    const response = await api.post(`/market-maps/admin/${mapId}/shapes/bulk`, { shapes });
    return response.data;
  },

  /**
   * Bulk delete shapes
   * @param {number} mapId - Market map ID
   * @param {array} shapeIds - Array of shape IDs to delete
   */
  bulkDeleteShapes: async (mapId, shapeIds) => {
    const response = await api.delete(`/market-maps/admin/${mapId}/shapes/bulk`, { data: { shape_ids: shapeIds } });
    return response.data;
  },

  // ============================================
  // UTILITY MARKER ENDPOINTS (Admin only)
  // ============================================

  /**
   * Get all utility markers for a map
   * @param {number} mapId - Market map ID
   */
  getUtilityMarkers: async (mapId) => {
    const response = await api.get(`/market-maps/admin/${mapId}/utility-markers`);
    return response.data;
  },

  /**
   * Create new utility marker
   * @param {number} mapId - Market map ID
   * @param {object} data - Utility marker data (name, latitude, longitude, icon_type)
   */
  createUtilityMarker: async (mapId, data) => {
    const response = await api.post(`/market-maps/admin/${mapId}/utility-markers`, data);
    return response.data;
  },

  /**
   * Update utility marker
   * @param {number} mapId - Market map ID
   * @param {number} markerId - Utility marker ID
   * @param {object} data - Updated data
   */
  updateUtilityMarker: async (mapId, markerId, data) => {
    const response = await api.put(`/market-maps/admin/${mapId}/utility-markers/${markerId}`, data);
    return response.data;
  },

  /**
   * Delete utility marker
   * @param {number} mapId - Market map ID
   * @param {number} markerId - Utility marker ID
   */
  deleteUtilityMarker: async (mapId, markerId) => {
    const response = await api.delete(`/market-maps/admin/${mapId}/utility-markers/${markerId}`);
    return response.data;
  },

  /**
   * Bulk create utility markers
   * @param {number} mapId - Market map ID
   * @param {array} markers - Array of utility marker data (name, latitude, longitude, icon_type)
   */
  bulkCreateUtilityMarkers: async (mapId, markers) => {
    const response = await api.post(`/market-maps/admin/${mapId}/utility-markers/bulk`, { markers });
    return response.data;
  },

  // ============================================
  // GEOJSON ENDPOINTS (Admin only)
  // ============================================

  /**
   * Export map as GeoJSON
   * @param {number} mapId - Market map ID
   * @param {object} options - Export options (layer_id, include_markers)
   */
  exportGeoJSON: async (mapId, options = {}) => {
    const params = {
      layer_id: options.layerId,
      include_markers: options.includeMarkers !== false ? 'true' : 'false',
    };
    const response = await api.get(`/market-maps/admin/${mapId}/geojson`, { params });
    return response.data;
  },

  /**
   * Import GeoJSON into map
   * @param {number} mapId - Market map ID
   * @param {object} data - Import data (geojson, layer_id, mode)
   */
  importGeoJSON: async (mapId, data) => {
    const response = await api.post(`/market-maps/admin/${mapId}/geojson`, data);
    return response.data;
  },

  /**
   * Download GeoJSON as file
   * @param {number} mapId - Market map ID
   * @param {string} filename - Download filename
   * @param {object} options - Export options
   */
  downloadGeoJSON: async (mapId, filename = 'export.geojson', options = {}) => {
    const result = await marketMapService.exportGeoJSON(mapId, options);
    if (result.status === 'success') {
      const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
    return result;
  },

  // ============================================
  // MARKER ICON ENDPOINTS
  // ============================================

  /**
   * Get all marker icons
   * @param {string} category - Optional category filter
   */
  getMarkerIcons: async (category = null) => {
    const params = category ? { category } : {};
    const response = await api.get('/market-maps/marker-icons', { params });
    return response.data;
  },

  /**
   * Upload marker icon
   * @param {File} file - Icon file (PNG, JPG, SVG)
   * @param {object} metadata - Icon metadata (name, category, anchor_x, anchor_y)
   */
  uploadMarkerIcon: async (file, metadata = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (metadata.name) formData.append('name', metadata.name);
    if (metadata.category) formData.append('category', metadata.category);
    if (metadata.anchor_x !== undefined) formData.append('anchor_x', metadata.anchor_x);
    if (metadata.anchor_y !== undefined) formData.append('anchor_y', metadata.anchor_y);

    const response = await api.post('/market-maps/marker-icons', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Update marker icon metadata
   * @param {number} iconId - Icon ID
   * @param {object} data - Updated metadata
   */
  updateMarkerIcon: async (iconId, data) => {
    const response = await api.put(`/market-maps/marker-icons/${iconId}`, data);
    return response.data;
  },

  /**
   * Delete marker icon
   * @param {number} iconId - Icon ID
   */
  deleteMarkerIcon: async (iconId) => {
    const response = await api.delete(`/market-maps/marker-icons/${iconId}`);
    return response.data;
  },

  // ============================================
  // HELPER METHODS
  // ============================================

  /**
   * Format price for display
   * @param {number} price - Price value
   * @param {string} unit - Price unit (VND, ty, trieu)
   */
  formatPrice: (price, unit = 'VND') => {
    if (!price) return 'Liên hệ';

    if (unit === 'ty' || price >= 1000000000) {
      const ty = price / 1000000000;
      return `${ty.toFixed(ty % 1 === 0 ? 0 : 2)} tỷ`;
    }

    if (unit === 'trieu' || price >= 1000000) {
      const trieu = price / 1000000;
      return `${trieu.toFixed(trieu % 1 === 0 ? 0 : 1)} triệu`;
    }

    return new Intl.NumberFormat('vi-VN').format(price) + ' VNĐ';
  },

  /**
   * Format area for display
   * @param {number} area - Area in m2
   */
  formatArea: (area) => {
    if (!area) return '';
    return `${new Intl.NumberFormat('vi-VN').format(area)} m²`;
  },

  /**
   * Get marker color based on price range
   * @param {number} price - Price value
   * @param {string} type - Map type (cho_thue/mua_ban)
   */
  getMarkerColor: (price, type = 'mua_ban') => {
    if (!price) return '#9CA3AF'; // gray for no price

    if (type === 'cho_thue') {
      // Rental prices (per month)
      if (price < 10000000) return '#22C55E'; // green: < 10 triệu
      if (price < 20000000) return '#EAB308'; // yellow: 10-20 triệu
      if (price < 50000000) return '#F97316'; // orange: 20-50 triệu
      return '#EF4444'; // red: > 50 triệu
    } else {
      // Sale prices
      if (price < 2000000000) return '#22C55E'; // green: < 2 tỷ
      if (price < 5000000000) return '#EAB308'; // yellow: 2-5 tỷ
      if (price < 10000000000) return '#F97316'; // orange: 5-10 tỷ
      return '#EF4444'; // red: > 10 tỷ
    }
  }
};

export default marketMapService;
